import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase.js';
import DOMPurify from 'dompurify';

const extractIdFromPath = (pathname) => {
  // expected path: /dashboard/news/:id or /dashboard/news
  const parts = pathname.split('/').filter(Boolean);
  const idx = parts.indexOf('news');
  if (idx >= 0 && parts.length > idx + 1) return parts[idx + 1];
  return null;
};

const isYouTube = (url) => {
  if (!url) return null;
  const m = url.match(/(?:v=|v\/|embed\/|youtu.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
};

const NewsDetail = ({ newsList = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [newsItem, setNewsItem] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [loading, setLoading] = useState(true);

  const idFromPath = extractIdFromPath(location.pathname);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (!idFromPath) {
          setNewsItem(null);
          return;
        }
        // try to find in provided list first
        const found = (newsList || []).find(n => n.id === idFromPath);
        if (found) {
          setNewsItem(found);
          setLoading(false);
          return;
        }
        const ref = doc(db, 'news', idFromPath);
        const snap = await getDoc(ref);
        if (snap.exists()) setNewsItem({ id: snap.id, ...snap.data() });
        else setNewsItem(null);
      } catch (err) {
        console.error('Error loading news detail:', err);
        setNewsItem(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [idFromPath, newsList]);

  if (!idFromPath) return <div className="admin-news-empty">Selecciona una noticia.</div>;
  if (loading) return <div className="admin-news-empty">Cargando noticia...</div>;
  if (!newsItem) return <div className="admin-news-empty">No se encontró la noticia.</div>;

  const ytId = isYouTube(newsItem.videoUrl);

  return (
    <div className="news-detail">
      <button className="lignums-button back-button" onClick={() => navigate(-1)}>Volver</button>
      <h2>{newsItem.title}</h2>
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newsItem.content || '') }} />
      {newsItem.videoUrl && (
        <div style={{ marginTop: '1rem' }}>
          {ytId ? (
            <div className="video-embed">
              {!showVideo ? (
                <button
                  className="video-placeholder"
                  onClick={() => setShowVideo(true)}
                  aria-label="Reproducir video"
                >
                  <img
                    src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                    alt="Thumbnail del video"
                    width="480"
                    height="270"
                  />
                  <span className="play-overlay" aria-hidden>
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.6)"/>
                      <polygon points="26,20 48,32 26,44" fill="#fff"/>
                    </svg>
                  </span>
                </button>
              ) : (
                <iframe
                  title="video"
                  width="480"
                  height="270"
                  src={`https://www.youtube.com/embed/${ytId}?rel=0`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
          ) : (
            <a href={newsItem.videoUrl} target="_blank" rel="noopener noreferrer">Abrir video</a>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsDetail;
